import { appServiceApi } from "../../../app/appServiceApi";
import { API_URL } from "../../../constants/apiUrl";
import { GetQueryArgsType } from "../../../types";
import {
  GetAllAssignProjectDataType,
  GetAllCompanyDataParamsType,
  GetAllCompanyDataType,
  GetAllWalletDataType,
  GetAllWalletParamsType,
  GetAllWalletTagDataType,
  GetAllWalletTagParamsType,
  GetCompanyWalletDataParamsType,
  GetCompanyWalletDataType,
  GetSingleCompanyDataType,
  GetSingleUserWalletRoleDataType,
  GetSingleWalletDataType,
  GetSingleWalletTagDataType,
  GetUsersWithDisbursementAccessDataType,
  GetUsersWithDisbursementAccessParamsType,
  GetUserWalletRoleByIdDataType,
  GetUserWalletRoleByUserIdDataType,
} from "../../../types/portal/companyOnboard";

export const companyOnboardApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCompany: builder.query<
      GetAllCompanyDataType,
      GetQueryArgsType<GetAllCompanyDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllCompany,
        ...queryArgs,
      }),
      providesTags: ["GetAllCompany"],
    }),
    getSingleCompany: builder.query<GetSingleCompanyDataType, string>({
      query: (id) => ({
        url: `${API_URL.getSingleCompany}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetSingleCompany", id }],
    }),
    createCompany: builder.mutation({
      query: (data) => ({
        url: API_URL.createCompany,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllCompany"];

        return [];
      },
    }),
    updateCompany: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateCompany}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetAllCompany",
            {
              type: "GetSingleCompany",
              id,
            },
          ];

        return [];
      },
    }),
    updatePin: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updatePin}/${id}/pin`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateWallet: builder.mutation({
      query: (data) => ({
        url: API_URL.updateWallet,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, _, data) => {
        if (result)
          return [
            "GetAllCompany",
            "GetBalanceCheck",
            {
              type: "GetSingleCompany",
              id: data.companyId,
            },
          ];

        return [];
      },
    }),

    assignProject: builder.mutation({
      query: (data) => ({
        url: API_URL.assignProject,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _, data) => {
        if (result)
          return [
            "GetAllCompany",
            {
              type: "GetSingleCompany",
              id: data.companyId,
            },
            {
              type: "GetAllAssignProject",
              id: data.companyId,
            },
          ];

        return [];
      },
    }),

    getAllAssignProject: builder.query<GetAllAssignProjectDataType, any>({
      query: ({ id, search }) => ({
        url: `${API_URL.getAllAssignProject}/${id}`,
        params: {
          search,
        },
      }),
      providesTags: (_, __, id) => [{ type: "GetAllAssignProject", id }],
    }),

    getCompanyWallet: builder.query<
      GetCompanyWalletDataType,
      GetQueryArgsType<GetCompanyWalletDataParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getCompanyWallet,
        ...queryArgs,
      }),
      providesTags: ["GetCompanyWallet"],
    }),

    // Wallet Tag APIs
    createWalletTag: builder.mutation({
      query: (data) => ({
        url: API_URL.createWalletTag,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllWalletTag"];

        return [];
      },
    }),

    getAllWalletTag: builder.query<
      GetAllWalletTagDataType,
      GetQueryArgsType<GetAllWalletTagParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllWalletTag,
        ...queryArgs,
      }),
      providesTags: ["GetAllWalletTag"],
    }),

    getSingleWalletTag: builder.query<GetSingleWalletTagDataType, number>({
      query: (id) => ({
        url: `${API_URL.getSingleWalletTag}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetSingleWalletTag", id }],
    }),

    updateWalletTag: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateWalletTag}/${id}`,
        method: "PUT",
        body: data,
      }),

      invalidatesTags: (result, _, data) => {
        if (result)
          return [
            "GetAllWalletTag",
            {
              type: "GetSingleWalletTag",
              id: data.id,
            },
          ];

        return [];
      },
    }),

    deleteWalletTag: builder.mutation({
      query: (id) => ({
        url: `${API_URL.deleteWalletTag}/${id}`,
        method: "DELETE",
      }),

      invalidatesTags: (result, _, data) => {
        if (result)
          return [
            "GetAllWalletTag",
            {
              type: "GetSingleWalletTag",
              id: data.id,
            },
          ];

        return [];
      },
    }),

    // Wallet APIs

    createWallet: builder.mutation({
      query: (data) => ({
        url: API_URL.createWallet,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllWallet"];

        return [];
      },
    }),

    updateWalletV2: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateWalletV2}/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (result, _, data) => {
        if (result)
          return [
            "GetAllWallet",
            {
              type: "GetSingleWallet",
              id: data.id,
            },
          ];

        return [];
      },
    }),

    getAllWallet: builder.query<
      GetAllWalletDataType,
      GetQueryArgsType<GetAllWalletParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllWallet,
        ...queryArgs,
      }),
      providesTags: ["GetAllWallet"],
    }),

    getSingleWallet: builder.query<GetSingleWalletDataType, number>({
      query: (id) => ({
        url: `${API_URL.getSingleWallet}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetSingleWallet", id }],
    }),

    // User Wallet Role APIs
    assignUserWalletRole: builder.mutation({
      query: (data) => ({
        url: API_URL.assignUserWalletRole,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetUsersWithDisbursementAccess",
            "GetUserWalletRoleById",
            "GetUserWalletRoleByUserId",
          ];

        return [];
      },
    }),

    updateUserWalletRole: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateUserWalletRole}/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (result) => {
        if (result)
          return [
            "GetUsersWithDisbursementAccess",
            "GetUserWalletRoleById",
            "GetUserWalletRoleByUserId",
          ];

        return [];
      },
    }),

    getUserWalletRoleById: builder.query<
      GetUserWalletRoleByIdDataType,
      { id?: number; role?: string; page?: number; limit?: number }
    >({
      query: ({ id, role, page, limit }) => ({
        url: `${API_URL.getUserWalletRoleById}/${id}/user-roles`,
        params: { role, page, limit },
      }),
      providesTags: (_, __, { id }) => [{ type: "GetUserWalletRoleById", id }],
    }),

    getUserWalletRoleByUserId: builder.query<
      GetUserWalletRoleByUserIdDataType,
      { id?: number; page?: number; limit?: number; search?: string }
    >({
      query: ({ id, page, limit, search }) => ({
        url: `${API_URL.getUserWalletRoleByUserId}/${id}/wallet-roles`,
        params: { page, limit, search },
      }),
      providesTags: (_, __, id) => [{ type: "GetUserWalletRoleByUserId" }],
    }),

    getUsersWithDisbursementAccess: builder.query<
      GetUsersWithDisbursementAccessDataType,
      GetQueryArgsType<GetUsersWithDisbursementAccessParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getUsersWithDisbursementAccess,
        ...queryArgs,
      }),
      providesTags: ["GetUsersWithDisbursementAccess"],
    }),

    getSingleUserWalletRole: builder.query<
      GetSingleUserWalletRoleDataType,
      { userId: number; walletId?: number }
    >({
      query: ({ userId, walletId }) => ({
        url: `${API_URL.getSingleUserWalletRole}/${walletId}/${userId}`,
      }),
      providesTags: ["GetSingleUserWalletRole"],
    }),
  }),
});

export const {
  useGetAllCompanyQuery,
  useGetSingleCompanyQuery,
  useUpdateCompanyMutation,
  useCreateCompanyMutation,
  useUpdatePinMutation,
  useUpdateWalletMutation,
  useAssignProjectMutation,
  useGetAllAssignProjectQuery,
  useGetCompanyWalletQuery,
  useCreateWalletTagMutation,
  useGetAllWalletTagQuery,
  useGetSingleWalletTagQuery,
  useUpdateWalletTagMutation,
  useDeleteWalletTagMutation,
  useCreateWalletMutation,
  useGetAllWalletQuery,
  useUpdateWalletV2Mutation,
  useGetSingleWalletQuery,
  useAssignUserWalletRoleMutation,
  useUpdateUserWalletRoleMutation,
  useGetUserWalletRoleByIdQuery,
  useGetUserWalletRoleByUserIdQuery,
  useGetUsersWithDisbursementAccessQuery,
  useGetSingleUserWalletRoleQuery,
} = companyOnboardApi;
